import { timeout } from 'widgets/toolbox/util';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';

const regexToCardsMapping = {
    Bijcard: /^5100081/, // CLARIFY: full length of card ?
    JCB: /^(?:2131|1800|35\d{3})\d{11}$/,
    Cup: /^(62[0-9]{14,17})$/,
    Visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
    Maestro: /^(5018|5020|5038|6304|6759|6761|6763)[0-9]{8,15}$/,
    'Master Card': /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/,
    Amex: /^3[47][0-9]{13}$/,
    DinersClub: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    // eslint-disable-next-line max-len
    Discover: /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
    Cartebancaire: /^(4|5|6)\d{15}/
};

/**
 * @param PaymentAccordionItem Base widget for extending
 * @returns Credit Card Payment item widget
 */
function PaymentAccordionItemCREDITCARDClassCreator(
    PaymentAccordionItem: import('widgets/checkout/PaymentAccordionItem').TPaymentAccordionItem
) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class PaymentAccordionItemCREDITCARD
     * @augments PaymentAccordionItem
     * @classdesc represents PaymentAccordionItemCREDITCARD component the with next features:
     * 1. Handles payment tab submit
     * 2. Validates payment tab child elements
     *
     * @example <caption>Example of PaymentAccordionItemCREDIT_CARD widget usage</caption>
     *  <section
     *      data-widget="paymentAccordionItem${paymentOption.ID}"
     *      data-widget-event-closeallitems="closeItems"
     *      data-widget-event-togglebillingform="toggleBillingForm"
     *      data-widget-event-accordionitemupdate="updateFocusedItem"
     *      data-method-id="${paymentOption.ID}"
     *      data-method-name="${paymentOption.name}"
     *      data-widget-event-openpanel="hidePaymentError"
     *      data-tau-payment-id="${paymentOption.ID}"
     *      class="b-payment_accordion-item"
     *  >
     *      <isinclude template="checkout/billing/paymentOptions/paymentTitle_CREDIT_CARD" />
     *      <div
     *          id="payment-details-${paymentOption.ID}"
     *          data-ref="accordionItemPanel"
     *          class="b-payment_accordion-content"
     *          data-tau="payment-details-${paymentOption.ID}"
     *      >
     *          <div class="b-payment_accordion-content_inner" data-ref="accordionItemPanelInner">
     *              ... inner content
     *          </div>
     *      </div>
     *  </section>
     */
    class PaymentAccordionItemCREDITCARD extends PaymentAccordionItem {
        prefs() {
            return {
                creditCard: {
                    type: 'dwfrm_billing_creditCardFields_cardType',
                    number: 'dwfrm_billing_creditCardFields_cardNumber',
                    expirationMonth: 'dwfrm_billing_creditCardFields_expirationMonth',
                    expirationYear: 'dwfrm_billing_creditCardFields_expirationYear',
                    save: 'dwfrm_billing_creditCardFields_saveCard',
                    securityCode: 'dwfrm_billing_creditCardFields_securityCode',
                    savedCardSecurityCode: 'dwfrm_billing_creditCardFields_savedCardSecurityCode'
                },
                sensitiveFields: ['number', 'expirationMonth', 'expirationYear', 'securityCode', 'savedCardSecurityCode'],
                savedCard: {
                    selectedCardID: 'dwfrm_billing_creditCardFields_selectedCardID',
                    securityCode: 'securityCode'
                },
                newPaymentBlock: 'newPaymentBlock',
                storedPaymentInstrumentsBlock: 'storedPaymentInstrumentsBlock',
                ...super.prefs()
            };
        }

        /**
         * @description Handles payment tab submit
         */
        process(): Promise<{[x: string]: string}> {
            return new Promise(resolve => {
                const fields = this.getFormFields();

                if (!this.isSavedCardsBlockActive()) {
                    fields[this.prefs().creditCard.type] = this.determineCardType(
                        fields[this.prefs().creditCard.number]
                    ) || 'Visa';

                    if (fields[this.prefs().creditCard.number]) {
                        fields[this.prefs().creditCard.number] = fields[this.prefs().creditCard.number].replace(/[\s-]/g, '');
                    }
                }

                resolve(fields);
            });
        }

        /**
         * @description Check is saved cards block active now
         * @returns Is saved cards block active
         */
        isSavedCardsBlockActive(): boolean {
            return this.ref('newPaymentBlock').hasAttr('hidden');
        }

        /**
         * @description Validates credit card payment tab child elements.
         * @returns validation result
         */
        isChildrenValid(): boolean {
            let valid = true;

            if (this.isSavedCardsBlockActive()) {
                return this.validateSavedCard();
            }

            this.eachField(item => {
                if (item.getName() && !this.isCreditCardField(item.getName())) {
                    return;
                }

                if (typeof item.validate === 'function' && !item.validate()) {
                    if (valid && item.setFocus) {
                        item.setFocus();
                    }

                    valid = false;
                }
            });

            return valid;
        }

        /**
         * @description Returns credit card form field id \ value map
         */
        getFormFields(): {[x: string]: string} {
            const fields = {};

            if (this.isSavedCardsBlockActive()) {
                return this.getSavedCardFields();
            }

            this.eachField(widget => {
                if (!(widget.skipSubmission && widget.skipSubmission())) {
                    const name = widget.getName && widget.getName();

                    if (name && !this.isSavedCardField(name)) {
                        fields[name] = widget.getValue();
                    }
                }
            });

            return fields;
        }

        /**
         * @description Determine card type by card number
         * @param cardNumber number of card
         */
        determineCardType(cardNumber: string): string | undefined {
            const cardNumberReplaced = cardNumber.replace(/[\s-]/g, '');

            return Object.keys(regexToCardsMapping)
                .filter(key => regexToCardsMapping[key] && regexToCardsMapping[key].test(cardNumberReplaced))
                .shift();
        }

        /**
         * @description Checks if field is saved credit card field
         * @param id - field id
         */
        isSavedCardField(id: string): boolean {
            return [this.prefs().savedCard.selectedCardID, this.prefs().savedCard.securityCode].includes(id);
        }

        /**
         * @description Process children fields with callback passed as param
         * @param fn - callback function
         */
        eachField(fn): Array<TBasicInputInstance> {
            const BasicInput = /** @type {BasicInput} */(this.getConstructor('basicInput'));

            if (this.items && this.items.length) {
                return this.items
                    .filter(item => item instanceof BasicInput)
                    .map(item => {
                        return fn(item);
                    });
            }

            return [];
        }

        /**
         * @description Returns saved credit card form field id \ value map
         */
        getSavedCardFields(): {[x: string]: string} {
            const fields = {};

            this.eachField(widget => {
                const name = widget.getName && widget.getName();

                if (widget.id === this.prefs().savedCard.selectedCardID && name) {
                    fields[name] = widget.getValue();
                } else if (
                    name === this.prefs().savedCard.securityCode
                    && widget.ref('self').data('selected')
                ) {
                    fields[name] = widget.getValue();
                }
            });

            return fields;
        }

        /**
         * @description Validates saved credit card
         */
        validateSavedCard(): boolean {
            let valid = true;

            this.eachField(item => {
                const isCurrentCVVField = (item.getName() === this.prefs().savedCard.securityCode)
                    && item.ref('self').data('selected');

                if (isCurrentCVVField && typeof item.validate === 'function' && !item.validate()) {
                    item.setFocus();

                    valid = false;
                }
            });

            return valid;
        }

        /**
         * @description Handles add new card click
         * @listens dom:click
         */
        addNewCard() {
            this.ref('storedPaymentInstrumentsBlock').hide();
            this.showNewCreditCard();

            this.updateState();
        }

        /**
         * @description Clears saved card sensitive data
         */
        clearSavedCardSensitiveData() {
            this.eachField(child => {
                if (child.id === this.prefs().savedCard.securityCode) {
                    child.setValue('', true);
                    child.clearError();
                }
            });
        }

        /**
         * @description Clear credit card fields
         */
        clearCardFields() {
            this.eachField((/** @type {basicInput} */child) => {
                if (this.isCreditCardField(child.id)) {
                    if (child.id !== this.prefs().creditCard.save) {
                        child.setValue('', true);
                        child.clearError();
                    } else {
                        child.setValue('true', true);
                    }
                }
            });
        }

        /**
         * @description Handles show saved payments click
         * @listens dom:click
         */
        showSavedPayments() {
            this.clearSavedCardSensitiveData();

            this.ref('storedPaymentInstrumentsBlock').show();
            this.ref('newPaymentBlock').hide();

            this.eachField(child => {
                if (child.id === this.prefs().savedCard.securityCode) {
                    child.setValue('', true);
                }
            });

            this.updateState();
            // class AccessibilityFocusMixin, method focusFirstInput
            timeout(() => this.callIfExists('focusFirstInput', 'storedPaymentInstrumentsBlock'), 200);
        }

        /**
         * @description Handles show new credit card form click
         * @listens dom:click
         */
        showNewCreditCard() {
            this.clearCardFields();
            this.ref('newPaymentBlock').show();
            // class AccessibilityFocusMixin, method focusFirstInput
            timeout(() => this.callIfExists('focusFirstInput', 'newPaymentBlock'), 200);
        }

        /**
         * @description Checks if field is credit card field
         * @param id - field id
         */
        isCreditCardField(id: string): boolean {
            let status = false;

            Object.keys(this.prefs().creditCard).forEach(field => {
                this.getById(this.prefs().creditCard[field], (/** @type {basicInput} */el) => {
                    if (el && el.id === id) {
                        status = true;
                    }
                });
            });

            return status;
        }

        /**
         * @description Handles select saved payment
         * @listens dom:click
         * @param element - saved payment
         */
        selectSavedPayment(element: TRefElementInstance) {
            this.getById(this.prefs().savedCard.selectedCardID, (savedCardID: TBasicInputInstance) => {
                savedCardID.setValue(element.data<string>('uuid'), true);
            });

            this.eachField(child => {
                if (child.getName() === this.prefs().savedCard.securityCode) {
                    const isCurrentCard = child.ref('self').data('uuid') === element.data('uuid');

                    child.ref('self').data('selected', isCurrentCard);

                    if (!isCurrentCard) {
                        child.setValue('', true);
                        child.clearError();
                    }
                }
            });
        }

        /**
         * @description Clears sensitive data
         */
        cleanupSensitiveData() {
            const sensitiveFields = this.prefs().sensitiveFields.map(field => this.prefs().creditCard[field]);

            this.eachField(child => {
                if (sensitiveFields.includes(child.id)) {
                    child.setValue('', true);
                    child.clearError();
                }
            });
        }
    }

    return PaymentAccordionItemCREDITCARD;
}

export type TPaymentAccordionItemCREDITCARD = ReturnType<typeof PaymentAccordionItemCREDITCARDClassCreator>;

export type TPaymentAccordionItemCREDITCARDInstance = InstanceType<TPaymentAccordionItemCREDITCARD>;

export default PaymentAccordionItemCREDITCARDClassCreator;
